/**
 * Utility functions for consistent user avatars across the application
 */

// Predefined color palette for user avatars (vibrant, accessible colors)
// Excludes green to avoid confusion with Shopify logo
export const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-teal-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-violet-500',
  'bg-fuchsia-500',
  'bg-rose-500',
];

/**
 * Generates a consistent color for a user based on their identifier
 * Uses email as the primary identifier for consistency across the app
 * Checks user.avatar_color property first for custom color
 * @param {string|number|object} userIdentifier - User ID, email, or user object
 * @returns {string} Tailwind CSS background color class
 */
export const getUserAvatarColor = (userIdentifier) => {
  if (!userIdentifier) return 'bg-gray-500'; // Fallback for missing user ID

  // If it's an object (user object), check for custom avatar_color first
  if (typeof userIdentifier === 'object') {
    // Check if user has a custom avatar color set
    if (userIdentifier.avatar_color && AVATAR_COLORS.includes(userIdentifier.avatar_color)) {
      return userIdentifier.avatar_color;
    }
    // Get identifier for hashing
    userIdentifier = userIdentifier.email || userIdentifier.id || userIdentifier.name || 'unknown';
  }

  // Convert identifier to string and create a simple hash
  const str = String(userIdentifier).toLowerCase().trim();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use absolute value and modulo to get consistent index
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * Gets user initials from name or email
 * @param {object} user - User object with firstName, lastName, name, or email
 * @returns {string} User initials (1-2 characters)
 */
export const getUserInitials = (user) => {
  if (!user) return '?';

  // Try firstName + lastName
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }

  // Try full name
  if (user.name) {
    const parts = user.name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  }

  // Fallback to email
  if (user.email) {
    return user.email.substring(0, 2).toUpperCase();
  }

  return '?';
};

/**
 * Gets the full user display name
 * @param {object} user - User object
 * @returns {string} Full user name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Unknown';

  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  if (user.name) {
    return user.name;
  }

  if (user.email) {
    return user.email;
  }

  return 'Unknown User';
};

/**
 * Gets consistent avatar props for a user (color + initials)
 * @param {object} user - User object
 * @returns {object} { colorClass, initials, displayName }
 */
export const getUserAvatarProps = (user) => {
  const userId = user?.id || user?.email || user?.name;
  return {
    colorClass: getUserAvatarColor(userId),
    initials: getUserInitials(user),
    displayName: getUserDisplayName(user),
  };
};

/**
 * Staff-member initials, matching the Staff Management page exactly.
 * Normally first + last initial; special-cases two "S" names (e.g. Sierra
 * Smith) to "Si" so they aren't all rendered as "SS".
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} 1-2 character initials, or '' if either name is missing
 */
export const generateInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return '';

  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();

  // Special case: If both initials are S, use first 2 letters of first name
  if (firstInitial === 'S' && lastInitial === 'S') {
    return firstName.substring(0, 2).charAt(0).toUpperCase() +
           firstName.substring(1, 2).toLowerCase();
  }

  return firstInitial + lastInitial;
};

/**
 * Builds a lookup Map (staff member id -> member) from the staff-members list,
 * so an order's sold_by_user_id can be resolved to that member's stored avatar.
 * @param {Array} staffMembers - list from staffMembersApi.getAll()
 * @returns {Map<string, object>}
 */
export const buildStaffById = (staffMembers) => {
  const map = new Map();
  (staffMembers || []).forEach((m) => {
    if (m && m.id != null) map.set(m.id, m);
  });
  return map;
};

/**
 * Resolves the avatar to show for an order's creator, matching the staff page:
 * the member's stored hex avatarColor + generateInitials. Falls back to the
 * order's denormalized sold_by_* name (and neutral gray) when the creator is
 * not a current staff sub-member.
 * @param {object} order - order with sold_by_user_id / sold_by_first_name / sold_by_last_name
 * @param {Map<string, object>} staffById - from buildStaffById()
 * @returns {object} { backgroundColor, initials, displayName }
 */
export const getOrderCreatorAvatar = (order, staffById) => {
  const member = (staffById && order?.sold_by_user_id)
    ? staffById.get(order.sold_by_user_id)
    : null;

  const firstName = member?.firstName || order?.sold_by_first_name;
  const lastName = member?.lastName || order?.sold_by_last_name;
  const displayName = (firstName && lastName) ? `${firstName} ${lastName}` : 'Staff Member';

  return {
    backgroundColor: member?.avatarColor || '#9ca3af', // gray-400 for unknown creator
    initials: generateInitials(firstName, lastName) || 'SM',
    displayName,
  };
};
